import React, { FunctionComponent, useRef } from 'react';
import withStyles from 'react-jss';
import { withRouter } from 'react-router-dom';
import { pathOr, isEmpty } from 'ramda';
import { Grid, Header, Icon } from 'semantic-ui-react';
import { format } from 'd3-format';
import { compose } from 'recompose';
import {
  ResponsiveContainer,
  ComposedChart,
  YAxis,
  Area,
  Tooltip,
  XAxis,
  Line,
} from 'recharts';

import DownloadWidget from 'components/DownloadWidget';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';
import CustomTooltip from 'components/CustomTooltip';
import { ClassProp, WithRouterProps } from 'helpers/types';
import { theme } from 'config';

import {
  tickFormatter,
  chartMargin,
  getResults,
  QueryResponse,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/KeyMetricGraphs/KeyMetricGraphs.helpers';
import { Timeslice } from 'scenes/Report/Report.helper';
import Calendar from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/KeyMetricGraphs/components/Calendar';
import { getBaseStyles } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/KeyMetricGraphs/KeyMetricGraphs.styles';
import { useMerchantOverview } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/MerchantOverview.context';

import KeyMetricDownload from '../KeyMetricDownload';

type Props = WithRouterProps & {
  classes: ClassProp<typeof styles>;
  response: QueryResponse;
  timeslice: Timeslice;
  dataSource: string;
  showOnline: boolean;
};

const styles = getBaseStyles(
  {
    chart: { height: 400 },
    chartContent: {
      padding: '1rem !important',
    },
    header: {
      display: 'flex !important',
      alignItems: 'center !important',
      fontWeight: '500 !important',
    },
  },
  { checkboxColor: theme.violet },
);

const formatter = format('.0f');

const tooltipFields = () => {
  const totalTransactions = {
    formatter,
    label: 'Total Transactions',
    value: 'totalNumberTransactions',
  };
  const onlineTransactions = {
    formatter,
    label: 'Online Sales',
    value: 'onlineNumberTransactions',
  };
  return [totalTransactions, onlineTransactions];
};

const renderHeader = (
  title: string,
  showOnline: boolean,
  classes: ClassProp<typeof styles>,
) => (
  <Header className={classes.header}>
    <Icon className={classes.headerIcon} name={'area chart'} />
    <span className={classes.headerLabel}>{title}</span>
    {showOnline && (
      <>
        <Icon className={classes.headerIcon2} name={'chart line'} />
        <span className={classes.headerLabel}>Online Transactions</span>
      </>
    )}
    <KeyMetricDownload />
  </Header>
);

const Transactions: FunctionComponent<Props> = ({
  classes,
  response,
  timeslice,
  dataSource,
  match,
  showOnline,
}) => {
  const { userProfile } = useMerchantOverview();
  const { startDate, endDate } = useDatePicker();
  const contentRef = useRef(null);
  const merchantId = pathOr(
    userProfile.merchantId,
    ['params', 'merchantId'],
    match,
  ) as string;

  const data = getResults(dataSource)(response);

  return (
    <>
      <div ref={contentRef} className={classes.ref}>
        <Grid.Row centered className={classes.chartContent}>
          <Grid.Column
            width={16}
            columns={1}
            textAlign="center"
            verticalAlign="middle"
            className={classes.noPadding}
          >
            <Calendar
              timeslice={timeslice}
              liftResults={data}
              chartType={'transactions'}
            />

            <Grid.Column>
              {isEmpty(data) ? (
                <Header as="h3">
                  There is no data to display at this time.
                </Header>
              ) : (
                <div className={classes.chart}>
                  <ResponsiveContainer>
                    <ComposedChart data={data} margin={chartMargin}>
                      <YAxis tickFormatter={tickFormatter('.0f')} />
                      <XAxis tick={false} scale="point" />

                      <Tooltip
                        content={
                          <CustomTooltip
                            fields={tooltipFields()}
                            xAxis={{
                              label: 'Day',
                              key: 'periodStart',
                              type: 'date',
                            }}
                          />
                        }
                      />

                      <Area
                        type="linear"
                        dataKey="totalNumberTransactions"
                        fill={theme.violet}
                        stroke={theme.violet}
                      />

                      {showOnline && (
                        <Line
                          type="linear"
                          dataKey="onlineNumberTransactions"
                          stroke="#150c24"
                          strokeWidth={2}
                          dot={false}
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Grid.Column>
          </Grid.Column>

          <Grid.Column width={16} className={classes.calendarLegend}>
            {renderHeader('Total Transactions', showOnline, classes)}
          </Grid.Column>
        </Grid.Row>
      </div>

      <DownloadWidget
        nodeRef={contentRef}
        className={classes.download}
        imageName="Transactions"
        csvParams={{
          startDate,
          endDate,
          merchantId,
          exportJobType: 'CLOF',
          runForDateType: 'CARD_TRANSACTION',
        }}
      />
    </>
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(Transactions);
